import type { ExpandedState, OnChangeFn, Row, SortingState } from "@tanstack/react-table"
import type { FC, RefObject } from "react"

import { useProductCostBreakdownColumns } from "../hooks/useProductCostBreakdownColumns"
import type { ProductCostBreakdownRow } from "../types"

import { ProductCostBreakdownExpandedContent } from "./ProductCostBreakdownExpandedContent"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import { PurchaseOrderLineCostBreakdownExpandedContent } from "@/screens/Procurement/CostBreakdown/components/PurchaseOrderLineCostBreakdownExpandedContent"

interface ProductCostBreakdownsTableProps {
  data: ProductCostBreakdownRow[]
  isLoading: boolean
  expandedState: ExpandedState
  sortingState: SortingState
  tableContainerRef: RefObject<HTMLDivElement>
  onExpandedChange: OnChangeFn<ExpandedState>
  onSortingChange: OnChangeFn<SortingState>
  onScroll: () => void
}

export const ProductCostBreakdownsTable: FC<ProductCostBreakdownsTableProps> = ({
  data,
  isLoading,
  expandedState,
  sortingState,
  tableContainerRef,
  onExpandedChange,
  onSortingChange,
  onScroll,
}) => {
  const columns = useProductCostBreakdownColumns()

  const renderExpandedContent = (row: Row<ProductCostBreakdownRow>) => {
    const { purchaseOrderLine, costBreakdown } = row.original

    return purchaseOrderLine?.costBreakdown ? (
      <PurchaseOrderLineCostBreakdownExpandedContent lineCostBreakdown={purchaseOrderLine.costBreakdown} />
    ) : (
      <ProductCostBreakdownExpandedContent costBreakdown={costBreakdown} />
    )
  }

  return (
    <AerosBaseTable
      features={{
        table: { stickyHeader: true },
        tanstackOptions: {
          data,
          columns,
          state: { sorting: sortingState, expanded: expandedState, isLoading },
          manualSorting: true,
          enableSortingRemoval: false,
          enableMultiSort: false,
          getRowCanExpand: () => true,
          renderExpandedContent,
          onSortingChange,
          onExpandedChange,
        },
      }}
      slotProps={{
        container: { ref: tableContainerRef, onScroll },
        bodyCellExpanded: { className: "bg-primary-100" },
      }}
      noDataMessage='No product cost breakdowns found'
    />
  )
}
